// inv/src/pages/authentication/CurrentPlanInfo.jsx
import axios from 'axios';
import { API_URL, MEMBERSHIP_NAMES } from '../../config';
import { AlertBox } from '../../components/ui/alertBox';
import CTAHover from '../../components/ui/CTAHover';
import { useMembershipAccess } from '../../hooks/membership';

const CurrentPlanInfo = (props) => {
    const user = props.user || {};
    const membershipUser = {
        ...props,
        ...user,
        membershipType: user.membershipType ?? props.membershipType,
        pendingMembershipType: user.pendingMembershipType ?? props.pendingMembershipType,
        status: user.status ?? props.status,
    };
    const membership = useMembershipAccess({ user: membershipUser, loading: false });

    const membershipType = membership.rawMembershipType;
    const membershipExpirationDate =
        user.membershipExpirationDate ?? props.membershipExpirationDate ?? null;
    const pendingMembershipType = membership.pendingMembershipType;
    const incompleteCheckout = user.incompleteCheckout ?? props.incompleteCheckout ?? null;

    const paddleCustomerId =
        user.paddleCustomerId
        ?? user.paddleUserId
        ?? props.paddleCustomerId
        ?? props.paddleUserId
        ?? null;

    const paddleSubscriptionId =
        user.paddleSubscriptionId ?? props.paddleSubscriptionId ?? null;

    const calculateDaysLeft = (date) =>
        Math.ceil(Math.max(new Date(date) - new Date(), 0) / 86_400_000);

    const formatDate = (date) =>
        new Date(date).toLocaleDateString('en-US', {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
        });

    const daysLeft = membershipExpirationDate
        ? calculateDaysLeft(membershipExpirationDate)
        : null;

    const handleManageClick = async (e) => {
        e.preventDefault();
        if (!paddleCustomerId) return;

        try {
            const { data } = await axios.post(
                `${API_URL}/customers/${paddleCustomerId}/portal-session`,
                { subscriptionIds: paddleSubscriptionId ? [paddleSubscriptionId] : [] },
                { withCredentials: true },
            );

            const link =
                data?.urls?.subscriptions?.[0]?.update_subscription_payment_method ||
                data?.urls?.general?.overview;

            if (link) window.open(link, '_blank', 'noopener,noreferrer');
            else throw new Error('portal link missing');
        } catch (e) {
            console.error('Customer‑portal error:', e);
            alert('Failed to open the billing portal. Please try again.');
        }
    };

    return (
        <div className="w-full p-4">
            <h3 className="text-base font-semibold text-gray-800 mb-3">
                Subscription details
            </h3>

            <div className="space-y-3">
                <p className="text-sm font-medium text-gray-800">
                    <span className="text-gray-600">Current plan:</span>{' '}
                    {MEMBERSHIP_NAMES[membershipType] || 'Free Plan'}
                </p>

                {membershipExpirationDate && (
                    <p className="text-sm font-medium text-gray-800">
                        <span className="text-gray-600">Valid Until:</span>{' '}
                        {formatDate(membershipExpirationDate)}
                        {daysLeft > 0 && ` (${daysLeft} days remaining)`}
                    </p>
                )}

                {pendingMembershipType && (
                    <AlertBox variant="yellow">
                        <p className="text-sm font-medium">
                            You have a pending plan upgrade/downgrade:
                        </p>
                        <p className="text-lg font-semibold text-yellow-800">
                            {MEMBERSHIP_NAMES[pendingMembershipType]}
                        </p>
                        {daysLeft > 0 && (
                            <p className="text-sm text-stone-700">
                                This plan will be applied in <strong>{daysLeft}</strong> days,
                                on <strong>{formatDate(membershipExpirationDate)}</strong>.
                            </p>
                        )}
                    </AlertBox>
                )}

                {incompleteCheckout && incompleteCheckout.planId && incompleteCheckout.membershipType && incompleteCheckout.interval && (
                    <AlertBox variant="orange">
                        <p className="text-sm font-medium">
                            You have an incomplete checkout for the{' '}
                            <strong>{MEMBERSHIP_NAMES[incompleteCheckout.membershipType]}</strong>{' '}
                            plan.
                        </p>
                        <p className="text-sm text-stone-700">
                            You can complete your payment at any time on the pricing page.
                        </p>
                    </AlertBox>
                )}

                {paddleCustomerId && (
                    <CTAHover
                        text="Manage subscription"
                        href="#"
                        align="left"
                        bgColor="bg-blue-600 text-white"
                        textColor="text-stone-800"
                        hoverTextColor="text-white"
                        className="mt-2"
                        onClick={handleManageClick}
                    />
                )}
            </div>
        </div>
    );
};

export default CurrentPlanInfo;
